// @flow
import { useSelector, useDispatch } from 'react-redux';
import { useCallback, useEffect } from 'react';
import {
	fetchOrders,
	selectOrder,
	fetchLastOrder,
	fetchOrderInvoice,
	syncPendingOrders,
	cancelSyncPendingOrders,
	cancelPendingOrder,
	poolActiveOrder,
	cancelPoolingActiveOrder,
	cancelVirtualOrder,
	poolAllLiveOrders,
	cancelPoolAllLiveOrders,
	setAllLiveOrders
} from '../actions/orders.js';
import type { OrderState } from '../reducers/orders';
import type { UserState } from '../reducers/user';
import { setCookie, deleteCookie } from '../utils';

const useOrder = () => {
	const orders = useSelector((state): OrderState => state.orders);
	const { pendingOrders = [], selectedOrder, liveOrders = [] } = orders;

	const { mobile } = useSelector((state): UserState => state.user);
	const dispatch = useDispatch();

	const fetchOrdersCb = useCallback(() => {
		dispatch(fetchOrders(mobile));
	}, [dispatch, mobile]);

	const fetchLastOrdersCb = useCallback(() => {
		dispatch(fetchLastOrder(mobile));
	}, [dispatch, mobile]);

	const handleSelectOrder = useCallback(
		(order: any) => {
			// if (order) {
			dispatch(selectOrder(order));
			// }
		},
		[dispatch]
	);

	const fetchOrderInvoiceCb = useCallback(
		(orderId: string, type: 'ORDER_ID' | 'SORDER_ID' | 'ACCCESS_CODE' = 'ORDER_ID') => {
			if (orderId) {
				dispatch(fetchOrderInvoice(orderId, type));
			}
		},
		[dispatch]
	);

	const syncPendingOrdersCb = useCallback(
		(delay?: number) => {
			dispatch(syncPendingOrders(mobile, delay));
		},
		[dispatch, mobile]
	);

	const cancelSyncPendingOrdersCb = useCallback(() => {
		dispatch(cancelSyncPendingOrders());
	}, [dispatch]);

	const cancelPendingOrderCb = useCallback(
		(orderId: string, updateCart: boolean) => {
			dispatch(cancelPendingOrder(orderId, updateCart));
		},
		[dispatch]
	);

	const poolActiveOrderCb = useCallback(
		(orderId: string) => {
			dispatch(poolActiveOrder(orderId));
		},
		[dispatch]
	);

	const cancelPoolingActiveOrderCb = useCallback(() => {
		dispatch(cancelPoolingActiveOrder());
	}, [dispatch]);

	const cancelVirtualOrderCb = useCallback(
		(orderId: string) => {
			dispatch(cancelVirtualOrder(orderId));
		},
		[dispatch]
	);

	const poolLiveOrdersCb = useCallback(
		(orders: Array<{ [key: string]: any }>) => {
			dispatch(poolAllLiveOrders(orders));
		},
		[dispatch]
	);

	const cancelPoolLiveOrdersCb = useCallback(() => {
		dispatch(cancelPoolAllLiveOrders());
	}, [dispatch]);

	const setLiveOrdersCb = useCallback((orders: Array<{[key:string]: any}> | null) => {
		dispatch(setAllLiveOrders(orders));
	}, [dispatch]);

	useEffect(() => {
		setCookie('selectedOrder', JSON.stringify(selectedOrder));
	}, [selectedOrder]);

	useEffect(() => {
		setCookie('pendingOrders', JSON.stringify(pendingOrders));
	}, [pendingOrders]);

	useEffect(() => {
		if (liveOrders) {
			setCookie('liveOrders', JSON.stringify(liveOrders));
		} else {
			deleteCookie('liveOrders');
		}
	}, [liveOrders]);

	return [
		orders,
		fetchLastOrdersCb,
		fetchOrdersCb,
		handleSelectOrder,
		fetchOrderInvoiceCb,
		syncPendingOrdersCb,
		cancelSyncPendingOrdersCb,
		cancelPendingOrderCb,
		poolActiveOrderCb,
		cancelPoolingActiveOrderCb,
		cancelVirtualOrderCb,
		poolLiveOrdersCb,
		cancelPoolLiveOrdersCb,
		setLiveOrdersCb,
	];
};

export default useOrder;
